import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NgKLoggerService } from '@kin/ngk-logger';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';
import { Observable } from 'rxjs';

import { AppContext } from 'app/app-config';
import { QuoteDraftStore } from 'app/home/global-store/quote-draft';
import { GuidService } from 'app/shared/services/guid/guid.service';

/**
 * Legacy (v1) Quote API Interceptor
 * used to attach the auth header to the request
 */
@Injectable()
export class LegacyQuotesApiInterceptor implements HttpInterceptor {
  @AppContext() private appContext!: AppContext;

  store = inject(QuoteDraftStore);
  logger = inject(NgKLoggerService);
  guidService = inject(GuidService);
  localStorage = inject(WA_LOCAL_STORAGE);

  /**
   * Interceptor for adding authorization and tracking headers
   */
  intercept(orgRequest: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Base URLs for which we send the auth token with encrypted `customer_input_response` ID and JWT
    const urlRoots = [this.appContext.apiRoot.quoteV1, this.appContext.apiRoot.quotingPlatform];
    // URLs this interceptor must leave alone: they either require no token or attach their own auth
    const urlsToSkip: string[] = [
      `${urlRoots[0]}/marketing_leads_for_new_states`,
      `${urlRoots[0]}/intro_data`,
      `${urlRoots[0]}/click_leads`,
      // MediaAlphaService attaches its own Authorization header (draft token OR coming-soon address token)
      `${urlRoots[0]}/home-insurance/results`,
    ];
    // URLs that do not require a token; initial POST to /addresses returns the auth token, so it's expected that the request does not contain auth token in header
    // but subsequent GET requests must include the token.
    const optionalUrlsToSkip = [`${urlRoots[0]}/addresses`];

    const shouldAddToken: boolean = LegacyQuotesApiInterceptor.checkUrl(urlRoots, urlsToSkip, orgRequest.url);
    const isOptional: boolean = LegacyQuotesApiInterceptor.isOptionalUrl(urlRoots, optionalUrlsToSkip, orgRequest.url);

    if (shouldAddToken) {
      let authToken = this.store.draftToken();
      if (!authToken) {
        this.logger.event({
          name: 'token-missing-from-quoteDraftStore',
          metaData: {
            message: `token missing from store for request: ${orgRequest.url}`,
            request: orgRequest,
          },
        });
        const tokenFromLocalStorage = this.localStorage?.getItem('draftToken');
        if (tokenFromLocalStorage) {
          this.logger.event({
            name: 'token-retrieved-from-local-storage',
            metaData: {
              message: `token retrieved from local storage for request: ${orgRequest.url}.`,
              request: orgRequest,
            },
          });
          authToken = tokenFromLocalStorage;
          this.store.patchStore({ draftToken: tokenFromLocalStorage });
        }
      }

      if (authToken) {
        const updatedRequest = LegacyQuotesApiInterceptor.addAuthHeader(authToken, orgRequest, this.guidService);
        return next.handle(updatedRequest);
      }

      if (!isOptional) {
        this.logger.event({
          name: 'token-missing',
          metaData: {
            message: `token missing for request: ${orgRequest.url}`,
            request: orgRequest,
          },
        });
      }
    }

    return next.handle(orgRequest);
  }

  /**
   * Clones the passed HTTP Req and adds auth and debug headers
   */
  static addAuthHeader(token: string, orgRequest: HttpRequest<unknown>, guidService: GuidService): HttpRequest<unknown> {
    return orgRequest.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'Session-Debug-Header': guidService.guid,
      },
    });
  }

  /**
   * ensures we only add headers for urls we care about
   */
  static checkUrl(rootUrls: string[], urlsToSkip: string[], reqUrl: string): boolean {
    const matchedRootUrls = rootUrls.filter((rootUrl) => reqUrl.includes(rootUrl));
    const matchedSkipUrls = urlsToSkip.includes(reqUrl);
    if (matchedRootUrls.length > 0 && !matchedSkipUrls) {
      return true;
    }
    return false;
  }

  /**
   * checks if a URL is in the optional list
   */
  static isOptionalUrl(rootUrls: string[], optionalUrls: string[], reqUrl: string): boolean {
    const matchedRootUrls = rootUrls.filter((rootUrl) => reqUrl.includes(rootUrl));
    return matchedRootUrls.length > 0 && optionalUrls.includes(reqUrl);
  }
}
